import { useMemo } from 'react'

import { sortArrayBy } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignRuralCategoryRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignRuralCategories']

type UseCampaignRuralCategoriesOptions = {
  performHttpRequests?: boolean,
  requestOptions?: CampaignRuralCategoryRequestOptions,
}

function useCampaignRuralCategories(options: UseCampaignRuralCategoriesOptions) {
  const { performHttpRequests = false, requestOptions } = options

  const {
    updatedEntities: { campaignRuralCategories: campaignRuralCategoriesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignRuralCategories } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignRuralCategories = useMemo(
    () => sortArrayBy(Object.values(campaignRuralCategories), 'asc', 'title'),
    [campaignRuralCategoriesUpdatedAt],
  )

  const filteredCampaignRuralCategoriesCount = filteredCampaignRuralCategories.length
  const hasFilteredCampaignRuralCategories = !!filteredCampaignRuralCategoriesCount

  const { loading: loadingCampaignRuralCategories } = useReduxAction(
    'campaignRuralCategories',
    'loadCampaignRuralCategories',
    {
      ...requestOptions,
    },
    [performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    filteredCampaignRuralCategories,
    filteredCampaignRuralCategoriesCount,
    hasFilteredCampaignRuralCategories,
    loading: loadingCampaignRuralCategories,
  }
}

export default useCampaignRuralCategories
